// 🧩 지그 사용량 막대 (2026-10-30 구독 플랜제 — 계획_구독플랜제_2026-10-30 §2, 대표: "개수가 아니라 한도 대비 %")
//   데이터 = 서버 callable getJigUsage() → { month, count, limit, pct, planCode, planLabel, nextPlanLabel, warned90, over }
//   환자정보 없음(개수·한도만). 구독 화면(AppSubscription·JigUsagePlan)·지그 화면(Jig.js) 세 곳이 같은 숫자를 본다.
//   90% 이상 경고 줄, 100% 초과면 "다음 달부터 {nextPlanLabel}으로 전환" 안내(프리미엄 초과는 별도 안내). 생성을 막지 않는다.
import React, { useEffect, useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

/** getJigUsage 조회 훅. enabled=false 면 호출하지 않는다. 실패는 error 문자열 + console.error(조용한 실패 금지) */
export function useJigUsage(enabled, deps = []) {
    const [usage, setUsage] = useState(null);     // null = 아직 모름(로딩·실패·비활성)
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const reload = useCallback(async () => {
        if (!enabled) return;
        setLoading(true);
        setError('');
        try {
            const res = await httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'getJigUsage')({});
            const d = res?.data;
            if (!d || typeof d.count !== 'number' || typeof d.limit !== 'number') throw new Error('응답 형식 오류');
            setUsage(d);
        } catch (e) {
            console.error('[지그 사용량] getJigUsage 조회 실패:', e?.code || e?.details?.code || e?.message || e);
            setUsage(null);
            setError('이번 달 사용량을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        } finally {
            setLoading(false);
        }
    }, [enabled]);   // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { reload(); }, [reload, ...deps]);   // eslint-disable-line react-hooks/exhaustive-deps
    return { usage, error, loading, reload };
}

/** 한도 대비 % — 서버 pct 우선, 없으면 count/limit 로 계산. limit 0 이면 0 */
export function usagePct(u) {
    if (!u) return 0;
    if (typeof u.pct === 'number' && Number.isFinite(u.pct)) return Math.max(0, Math.round(u.pct));
    return u.limit > 0 ? Math.max(0, Math.round((u.count / u.limit) * 100)) : 0;
}

const barColor = (pct, over) => (over || pct >= 100) ? '#dc2626' : pct >= 90 ? '#d97706' : '#6366f1';

/**
 * 사용량 막대
 *  compact=true : 지그 화면 상단용 — "% 만" 크게, 작은 글씨 없음(개수는 title 로만)
 *  compact=false: 구독 화면용 — 큰 "이번 달 사용 41%", 작은 "(300개 중 123개)", 플랜 이름, 90%/초과 안내
 */
export default function JigUsageBar({ usage, error, loading, compact = false }) {
    if (error) return <div style={{ fontSize: 12.5, color: '#dc2626' }}>{error}</div>;
    if (!usage) return <div style={{ fontSize: 12.5, color: '#94a3b8' }}>{loading ? '사용량 확인 중…' : '—'}</div>;
    const pct = usagePct(usage);
    const width = Math.min(100, pct);
    const over = usage.over === true || pct > 100;
    const warn = !over && (usage.warned90 === true || pct >= 90);
    const color = barColor(pct, over);
    const countText = `(${(usage.limit || 0).toLocaleString('ko-KR')}개 중 ${(usage.count || 0).toLocaleString('ko-KR')}개)`;
    const track = { height: compact ? 6 : 10, borderRadius: 999, backgroundColor: '#e2e8f0', overflow: 'hidden' };
    const fill = { width: `${width}%`, height: '100%', backgroundColor: color, transition: 'width .3s' };

    if (compact) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }} title={`이번 달 지그 ${countText}`}>
                <span style={{ fontSize: 15, fontWeight: 800, color, whiteSpace: 'nowrap' }}>{`이번 달 사용 ${pct}%`}</span>
                <div style={{ ...track, flex: 1, minWidth: 80 }}><div style={fill} /></div>
                {usage.planLabel && <span style={{ fontSize: 11.5, color: '#64748b', whiteSpace: 'nowrap' }}>{usage.planLabel}</span>}
            </div>
        );
    }
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 22, fontWeight: 800, color }}>{`이번 달 사용 ${pct}%`}</span>
                <span style={{ fontSize: 12.5, color: '#64748b' }}>{countText}</span>
                {usage.planLabel && <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: '#4338ca', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 999, padding: '2px 10px' }}>{`${usage.planLabel} 플랜`}</span>}
            </div>
            <div style={track}><div style={fill} /></div>
            {warn && (
                <div style={{ fontSize: 12.5, color: '#92400e', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px' }}>
                    {'이번 달 한도의 90%에 도달했습니다. 한도를 넘어도 생성은 멈추지 않지만, 넘기면 다음 달부터 다음 플랜으로 전환됩니다.'}
                </div>
            )}
            {over && (
                <div style={{ fontSize: 12.5, color: '#991b1b', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '6px 10px' }}>
                    {usage.nextPlanLabel
                        ? `이번 달 한도를 넘었습니다. 다음 달부터 ${usage.nextPlanLabel}으로 전환됩니다.`
                        : '이번 달 한도를 넘었습니다. 프리미엄 한도 초과는 별도로 안내드립니다.'}
                </div>
            )}
        </div>
    );
}
